import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Outlet } from 'react-router-dom';

import { useGetAgreementsBuildingsQuery } from '@client/api';
import { Card, EmptyPlaceholder, Pagination, useRouteDocumentTitle } from '@client/toolkit';

import { AgreementBuildingItem } from './components/agreement-building-item.component';
import { AgreementListToolbar } from './components/agreement-list-toolbar.component';
import { AgreementStatisticsHeader } from './components/agreement-statistics-header.component';
import { AgreementUnassignedItem } from './components/agreement-unassigned-item.component';
import { useAgreementListSearchQueryParams } from './utils/use-agreement-list-query-params';
import { AgreementsPaths } from '../agreements.router';
import { PageContentAnimation } from '../../../components/layout';

export function AgreementListRoute() {
  const { t } = useTranslation('agreements');
  const { t: tCommon } = useTranslation();
  const { t: tGlobal } = useTranslation('global');

  useRouteDocumentTitle(t('documentTitle'), AgreementsPaths.AgreementList);

  const [searchQueryParams] = useAgreementListSearchQueryParams();

  const pageSize = 50;
  const [page, setPage] = useState(0);

  const { data, isLoading, isFetching } = useGetAgreementsBuildingsQuery(
    {
      'pagination.Limit': pageSize,
      'pagination.Offset': page * pageSize,
      search: searchQueryParams.agreementsSearch ? searchQueryParams.agreementsSearch : undefined,
    },
    {
      refetchOnMountOrArgChange: true,
    },
  );

  const buildings = useMemo(() => data?.items ?? [], [data?.items]);
  const totalCount = data?.totalCount ?? 0;
  const unassignedCount = data?.unassignedAgreementCount ?? 0;

  // Calculate total agreements for toolbar (buildings + unassigned)
  const totalAgreements = useMemo(() => {
    const buildingAgreements = buildings.reduce((sum, b) => sum + (b.agreementCount ?? 0), 0);
    return buildingAgreements + unassignedCount;
  }, [buildings, unassignedCount]);

  return (
    <PageContentAnimation>
      <Card className="mb-4" overflowX={false}>
        <AgreementListToolbar totalCount={totalAgreements} isFetching={isFetching} />

        {isFetching && !!searchQueryParams.agreementsSearch && (
          <div className="flex justify-center py-8">
            <div className="w-8 h-8 border-2 border-brand-primary rounded-full animate-spin border-r-transparent" />
          </div>
        )}

        {!(isFetching && !!searchQueryParams.agreementsSearch) && (
          <>
            {!isLoading && buildings.length <= 0 && unassignedCount <= 0 && (
              <div className="px-4 py-4">
                <EmptyPlaceholder>
                  {tCommon('noEntityFound', {
                    entity: tGlobal('entity.agreement', { context: 'plural' }).toLowerCase(),
                  })}
                </EmptyPlaceholder>
              </div>
            )}

            {!isLoading && (buildings.length > 0 || unassignedCount > 0) && <AgreementStatisticsHeader />}

            {isLoading && !searchQueryParams.agreementsSearch && Array.from(Array(5).keys()).map((x) => <AgreementBuildingItem key={x} isFetching />)}

            {!isLoading &&
              buildings.map((building) => (
                <AgreementBuildingItem key={building.buildingId} building={building} isFetching={false} search={searchQueryParams.agreementsSearch ?? undefined} />
              ))}

            {!isLoading && <AgreementUnassignedItem unassignedCount={unassignedCount} isFetching={false} search={searchQueryParams.agreementsSearch ?? undefined} />}

            <Pagination totalCount={totalCount} currentPage={page} setPage={setPage} pageSize={pageSize} />
          </>
        )}
      </Card>
      <Outlet />
    </PageContentAnimation>
  );
}
